import { useEffect, useState } from 'react';
import { KeyRound, RefreshCw, Pause, Play, Trash2, Plus, Moon, Smartphone, Power, X } from 'lucide-react';
import { api, type OpsModel, type Account } from '../api.js';
import { subscribePush, registerPasskey } from '../session.js';
import { applyTheme, currentTheme } from '../theme.js';
import { Screen, Section, Rows, Btn, Badge, Field, Toggle, Facts, Seg, Spinner, ago } from '../ui/kit.js';
import { AddAccountSheet } from './AddAccountSheet.js';
import { reporter } from '../pwa/report.js';

// The control room: the accounts (names and long-lived tokens on the box),
// spend measured here, the engine, notification devices, security, and the
// box itself — history graphs, deployed build vs main, cleanup, restart,
// retention, and the four thresholds the guardians read.

interface Health {
  disk: { totalGb: number; freeGb: number; usedRatio: number | null } | null;
  memory: { freeGb: number; totalGb: number };
  load: number[];
  uptimeS: number;
  webhook: { lastDeliveryAt: string | null; total: number };
  running: number;
  queued: number;
  attempts: { jobId: string; title: string; attempt: number | null; unit: string | null; alive: boolean; unitActive: boolean | null; since: string | null }[];
  enginePaused: boolean;
  deployedRelease: string | null;
  mainSha: string | null;
}
interface SettingsData {
  concurrency: number;
  enginePaused: boolean;
  jobDefaults: { model: string; effort: string | null; mode: 'hold' | 'ship' };
  quietHours: { start: string; end: string } | null;
  retention: { logsDays: number; shotsDays: number; artifactsDays: number };
  watch: { longCommandNotifyMin: number; idleNotifyMin: number; snapshotEveryMin: number; branchPushWithinMin: number };
  infraMonthlyEur: number | null;
  accounts: Account[];
  models: OpsModel[];
  cliVersion: string;
}
interface Spend {
  byDay: { day: string; usd: number; jobs: number }[];
  byModel: { model: string; usd: number; jobs: number }[];
  byAccount: { accountId: string | null; usd: number; attempts: number }[];
  today: { usd: number; jobs: number };
  week: { usd: number; jobs: number };
  month30: { usd: number; jobs: number };
  thisMonth: number;
  lastMonth: number;
  tokens30d: { input: number | null; output: number | null; cacheRead: number | null; cacheCreate: number | null };
  perJob: { id: string; title: string; model: string; kind: string; usd: number; created_at: string; account?: string | null }[];
  infraMonthlyEur: number | null;
}
interface HistorySample {
  ts: string;
  cpu_pct: number | null;
  mem_used_gb: number | null;
  mem_total_gb: number | null;
  disk_used_gb: number | null;
  disk_total_gb: number | null;
}

export function Settings() {
  const [settings, setSettings] = useState<SettingsData | null>(null);
  const [accounts, setAccounts] = useState<(Account & { limitedUntil?: string | null; running?: number; spend30d?: number })[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [cap, setCap] = useState<{ configuredSlots: number; fullLoadSlots: number; oversubscribed: boolean; note: string } | null>(null);
  const [howToAdd, setHowToAdd] = useState('');
  const [health, setHealth] = useState<Health | null>(null);
  const [spend, setSpend] = useState<Spend | null>(null);
  const [history, setHistory] = useState<HistorySample[]>([]);
  const [sessions, setSessions] = useState<{ id: string; created_at: string; last_seen_at: string; ip: string | null; user_agent: string | null }[]>([]);
  const [currentSession, setCurrentSession] = useState('');
  const [passkeys, setPasskeys] = useState<{ id: string; label: string | null; created_at: string; last_used_at: string | null }[]>([]);
  const [devices, setDevices] = useState<{ id: string; device_label: string | null; last_ok_at: string | null; expired_at: string | null }[]>([]);
  const [busy, setBusy] = useState(false);
  const [dark, setDark] = useState(currentTheme() === 'dark');

  const load = () => {
    void api.get<SettingsData>('/api/settings').then(setSettings).catch(reporter('settings'));
    void api
      .get<{ accounts: typeof accounts; howToAdd: string }>('/api/accounts')
      .then((r) => {
        setAccounts(r.accounts);
        setHowToAdd(r.howToAdd);
      })
      .catch(reporter('the accounts'));
    api
      .get<{ configuredSlots: number; fullLoadSlots: number; oversubscribed: boolean; note: string }>('/api/capacity')
      .then(setCap)
      .catch(() => undefined);
    void api.get<Health>('/api/system/health').then(setHealth).catch(reporter('settings'));
    void api.get<Spend>('/api/stats/spend').then(setSpend).catch(reporter('settings'));
    void api.get<{ samples: HistorySample[] }>('/api/system/health/history').then((r) => setHistory(r.samples)).catch(reporter('settings'));
    void api
      .get<{ sessions: typeof sessions; current: string }>('/api/auth/sessions')
      .then((r) => {
        setSessions(r.sessions);
        setCurrentSession(r.current);
      })
      .catch(reporter('settings'));
    void api.get<{ owner: boolean; passkeys: typeof passkeys }>('/api/auth/me').then((r) => setPasskeys(r.passkeys)).catch(reporter('settings'));
    void api.get<{ subscriptions: typeof devices }>('/api/push/subscriptions').then((r) => setDevices(r.subscriptions)).catch(reporter('settings'));
  };
  useEffect(load, []);

  const put = (body: Record<string, unknown>) => void api.put('/api/settings', body).then(load).catch(reporter('saving the setting'));
  const defaultModel = settings?.models.find((m) => m.id === settings.jobDefaults.model);

  return (
    <Screen title="Settings" sub="The accounts, the engine, the money, the box.">
      {/* ── Claude accounts: names and long-lived tokens on the box ───── */}
      <Section title="Claude accounts" aside={`${accounts.length} configured`}>
        <Rows>
          {accounts.map((a) => (
            <div key={a.id} className="ops-row" data-testid={`account-${a.id}`}>
              <div className="ops-row__main">
                <div className="ops-row__title">
                  {a.label} <span className="ops-sha">{a.id}</span>
                </div>
                <div className="ops-row__sub">
                  {a.limitedUntil ? `at its limit until ${new Date(a.limitedUntil).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · ` : ''}
                  {a.running ? `${a.running} running · ` : ''}
                  {a.spend30d != null ? `$${a.spend30d.toFixed(2)} in 30 days` : ''}
                </div>
              </div>
              <div className="ops-row__end">{a.limitedUntil ? <Badge tone="bad">limit</Badge> : <Badge tone="ok">token</Badge>}</div>
            </div>
          ))}
          {!accounts.length ? (
            <div className="ops-row">
              <div className="ops-row__main">
                <div className="ops-row__title">No account configured</div>
                <div className="ops-row__sub">Nothing can start until one is.</div>
              </div>
            </div>
          ) : null}
        </Rows>
        <div className="ops-add__cta">
          <Btn kind="primary" onClick={() => setAddOpen(true)} data-testid="add-account">
            <Plus size={15} /> Add an account
          </Btn>
        </div>
        <p className="ops-hint ops-hint--after" data-testid="accounts-how">
          Each account is one long-lived token from <span className="ops-sha">claude setup-token</span>.{' '}
          {howToAdd || 'The box runs it for you and proves the token against the API before keeping it.'} Nothing on the box
          copies or refreshes a credential; a job's attempt gets exactly one token in its environment, and an account's own
          email cannot be read from that token — so what is shown is the label you gave it.
        </p>
        <p className="ops-hint ops-hint--after">
          Capacity is a matrix: an account can be out of one model line and still have another.{' '}
          <a href="/capacity">See what has room right now.</a>
        </p>
      </Section>

      {/* ── Spend ───────────────────────────────────────────────────────── */}
      {spend ? (
        <Section title="Spend" aside={`this month $${spend.thisMonth.toFixed(2)} · last $${spend.lastMonth.toFixed(2)}`}>
          <div className="ops-card">
            <SpendChart days={spend.byDay} />
            <div className="ops-tokenrow">
              {spend.byModel.map((m) => (
                <span key={m.model}>
                  <b>${m.usd.toFixed(2)}</b> {m.model} ({m.jobs})
                </span>
              ))}
              {(spend.byAccount ?? []).length > 1
                ? spend.byAccount.map((a) => (
                    <span key={a.accountId ?? 'none'} data-testid={`spend-account-${a.accountId ?? 'none'}`}>
                      <b>${a.usd.toFixed(2)}</b> {accounts.find((x) => x.id === a.accountId)?.label ?? a.accountId ?? 'no account'}
                    </span>
                  ))
                : null}
              <span style={{ marginLeft: 'auto' }}>
                <b>{spend.infraMonthlyEur != null ? `€${spend.infraMonthlyEur}` : '—'}</b> infra / month
                <button
                  type="button"
                  className="ops-linklike"
                  style={{ padding: '0 8px' }}
                  onClick={() => {
                    const v = prompt('Fixed infrastructure per month, EUR (the boxes)', String(spend.infraMonthlyEur ?? ''));
                    if (v !== null) put({ infraMonthlyEur: v === '' ? null : Number(v) });
                  }}
                >
                  edit
                </button>
              </span>
            </div>
          </div>
          {spend.perJob.length ? (
            <Rows>
              {spend.perJob.slice(0, 10).map((j) => (
                <div key={j.id} className="ops-row" style={{ minHeight: 44 }}>
                  <div className="ops-row__main">
                    <div className="ops-row__sub" style={{ color: 'var(--ops-ink)' }}>{j.title}</div>
                  </div>
                  <div className="ops-row__end" style={{ fontFamily: 'var(--ops-mono)', fontSize: 12 }}>
                    ${j.usd.toFixed(2)}
                  </div>
                </div>
              ))}
            </Rows>
          ) : null}
        </Section>
      ) : null}

      {/* ── Engine ──────────────────────────────────────────────────────── */}
      {settings ? (
        <Section title="Engine" aside={settings.cliVersion ? `Claude Code ${settings.cliVersion.replace(/\s*\(.*\)$/, '')}` : undefined}>
          <Rows>
            <div className="ops-row">
              <div className="ops-row__main">
                <div className="ops-row__title">Queue</div>
                <div className="ops-row__sub">{settings.enginePaused ? 'Paused. Nothing starts and nothing continues.' : 'Running.'}</div>
              </div>
              <Btn kind={settings.enginePaused ? 'primary' : 'quiet'} onClick={() => put({ enginePaused: !settings.enginePaused })} data-testid="engine-pause">
                {settings.enginePaused ? <Play size={15} /> : <Pause size={15} />}
                {settings.enginePaused ? 'Resume' : 'Pause'}
              </Btn>
            </div>
            {/* V8 §9. This was a "Two jobs at once" toggle bound to a
                setting nothing reads any more: the scheduler runs as many
                jobs as there are SLOTS. A control that does nothing is the
                thing V2 went out of its way to remove, so it is the real
                control now, with the measured truth beside it. */}
            <div className="ops-row">
              <div className="ops-row__main">
                <div className="ops-row__title">Jobs at once</div>
                <div className="ops-row__sub">
                  Each slot is its own ports, database, browser profile and memory ceiling.
                  {cap ? ` This box carries ${cap.fullLoadSlots} job${cap.fullLoadSlots === 1 ? '' : 's'} at full load.` : ''}
                </div>
                {/* The warning belongs to the control that causes it, not
                    floating beside it: a bare paragraph dropped between rows
                    sits flush against the frame, which the layout lint
                    measured at 2px and was right about. */}
                {cap?.oversubscribed ? <div className="ops-row__sub ops-row__sub--warn">{cap.note}</div> : null}
              </div>
              {/* A Seg sizes to its label, and "1" is nine pixels wide — the
                  layout lint measured exactly that and was right. These are
                  pills with a real tap area of their own. */}
              <div className="ops-row__end ops-slots">
                {[1, 2, 3, 4].map((n) => (
                  <button
                    type="button"
                    key={n}
                    className={`ops-slots__opt${(cap?.configuredSlots ?? 2) === n ? ' ops-slots__opt--on' : ''}`}
                    aria-pressed={(cap?.configuredSlots ?? 2) === n}
                    aria-label={`${n} job${n === 1 ? '' : 's'} at once`}
                    onClick={() => {
                      void api
                        .put('/api/settings', { slots: n })
                        .then(() => api.get<typeof cap>('/api/capacity').then(setCap))
                        .catch(reporter('the slot count'));
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="ops-row">
              <div className="ops-row__main">
                <div className="ops-row__title">Quiet hours</div>
                <div className="ops-row__sub">Pushes hold their tongue between these times (Nairobi). The inbox still fills.</div>
              </div>
              <div className="ops-row__end" style={{ gap: 4 }}>
                <input
                  className="ops-input"
                  type="time"
                  style={{ width: 92, minHeight: 40 }}
                  defaultValue={settings.quietHours?.start ?? ''}
                  onBlur={(e) => {
                    const start = e.target.value;
                    put({ quietHours: start && settings.quietHours?.end ? { start, end: settings.quietHours.end } : start ? { start, end: '07:00' } : null });
                  }}
                />
                <span>–</span>
                <input
                  className="ops-input"
                  type="time"
                  style={{ width: 92, minHeight: 40 }}
                  defaultValue={settings.quietHours?.end ?? ''}
                  onBlur={(e) => {
                    const end = e.target.value;
                    put({ quietHours: end && settings.quietHours?.start ? { start: settings.quietHours.start, end } : null });
                  }}
                />
              </div>
            </div>
          </Rows>
          <div style={{ height: 12 }} />
          <Field label="Default model and effort for new jobs">
            <Seg
              big
              value={settings.jobDefaults.model}
              onChange={(m) => put({ jobDefaults: { ...settings.jobDefaults, model: m } })}
              options={settings.models.map((m) => ({
                value: m.id,
                label: (
                  <>
                    <span>{m.label}</span>
                    <small className="ops-modelid">{m.cli}</small>
                  </>
                ),
              }))}
            />
            {defaultModel?.efforts.length ? (
              <div style={{ marginTop: 8 }}>
                <Seg value={settings.jobDefaults.effort ?? 'high'} onChange={(e) => put({ jobDefaults: { ...settings.jobDefaults, effort: e } })} options={defaultModel.efforts.map((e) => ({ value: e, label: e }))} />
              </div>
            ) : null}
          </Field>
          <div style={{ height: 12 }} />
          <Field label="Default mode">
            <Seg
              value={settings.jobDefaults.mode}
              onChange={(m) => put({ jobDefaults: { ...settings.jobDefaults, mode: m } })}
              options={[
                { value: 'hold', label: 'Hold for review' },
                { value: 'ship', label: 'Ship on green', ship: true },
              ]}
            />
          </Field>
          <div style={{ height: 14 }} />
          <Field label="When the daemon says something, and how often it pushes (minutes)">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} data-testid="watch-settings">
              {(
                [
                  ['longCommandNotifyMin', 'a command open for'],
                  ['idleNotifyMin', 'no event for'],
                  ['snapshotEveryMin', 'snapshot every'],
                  ['branchPushWithinMin', 'push a commit within'],
                ] as const
              ).map(([k, label]) => (
                <label key={k} style={{ flex: '1 1 120px', fontSize: 11.5, color: 'var(--ops-ink-subtle)' }}>
                  {label}
                  <input
                    className="ops-input"
                    style={{ marginTop: 4 }}
                    inputMode="decimal"
                    data-testid={`watch-${k}`}
                    defaultValue={settings.watch?.[k]}
                    onBlur={(e) => put({ watch: { [k]: Number(e.target.value) || settings.watch?.[k] } })}
                  />
                </label>
              ))}
            </div>
          </Field>
          <p className="ops-hint">
            The daemon never kills a session and never pauses a job on its own. A command open past the first number and a quiet session past the second reach your phone as notifications; uncommitted work goes to a side ref on origin every third; a commit the session left unpushed is pushed after the fourth.
          </p>
        </Section>
      ) : null}

      {/* ── Appearance ──────────────────────────────────────────────────── */}
      <Section title="Appearance">
        <Rows>
          <div className="ops-row">
            <div className="ops-row__main">
              <div className="ops-row__title">Dark theme</div>
              <div className="ops-row__sub">The night control room. Light is the default.</div>
            </div>
            <div className="ops-row__end">
              <Moon size={16} />
              <Toggle
                checked={dark}
                onChange={(v) => {
                  setDark(v);
                  applyTheme(v ? 'dark' : 'light');
                  put({ theme: v ? 'dark' : 'light' });
                }}
                label="Dark theme"
              />
            </div>
          </div>
        </Rows>
      </Section>

      {/* ── Notifications ───────────────────────────────────────────────── */}
      <Section title="Notification devices">
        <Rows>
          {devices
            .filter((d) => !d.expired_at)
            .map((d) => (
              <div key={d.id} className="ops-row">
                <div className="ops-row__main">
                  <div className="ops-row__title">
                    <Smartphone size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
                    {d.device_label ?? 'Device'}
                  </div>
                  <div className="ops-row__sub">{d.last_ok_at ? `last push ${ago(d.last_ok_at)} ago` : 'no push yet'}</div>
                </div>
                <Btn kind="ghost" onClick={() => void api.del(`/api/push/subscriptions/${d.id}`).then(load).catch(reporter('removing the device'))}>
                  <X size={15} />
                </Btn>
              </div>
            ))}
          <div className="ops-row">
            <div className="ops-row__main">
              <div className="ops-row__title">This device</div>
              <div className="ops-row__sub">{typeof Notification !== 'undefined' && Notification.permission === 'granted' ? 'Push allowed here.' : 'Push not allowed yet.'}</div>
            </div>
            <Btn
              kind="quiet"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const perm = await Notification.requestPermission();
                  if (perm === 'granted') {
                    await subscribePush();
                    await api.post('/api/push/test');
                  }
                } finally {
                  setBusy(false);
                  load();
                }
              }}
            >
              {busy ? <Spinner /> : 'Test push'}
            </Btn>
          </div>
        </Rows>
        <p className="ops-hint">The per-event matrix lives on the Notifications screen.</p>
      </Section>

      {/* ── Security ────────────────────────────────────────────────────── */}
      <Section
        title="Security"
        aside={
          <button type="button" className="ops-linklike" onClick={() => void registerPasskey('Added in Settings').then(load).catch(reporter('settings'))}>
            <Plus size={13} style={{ verticalAlign: '-2px' }} /> Passkey
          </button>
        }
      >
        <Rows>
          {passkeys.map((k) => (
            <div key={k.id} className="ops-row">
              <div className="ops-row__main">
                <div className="ops-row__title">
                  <KeyRound size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
                  {k.label ?? 'Passkey'}
                </div>
                <div className="ops-row__sub">
                  added {ago(k.created_at)} ago{k.last_used_at ? ` · used ${ago(k.last_used_at)} ago` : ''}
                </div>
              </div>
              {passkeys.length > 1 ? (
                <Btn kind="ghost" onClick={() => void api.post(`/api/auth/passkeys/${k.id}/delete`).then(load).catch(reporter('removing the passkey'))}>
                  <X size={15} />
                </Btn>
              ) : null}
            </div>
          ))}
          {sessions.map((s) => (
            <div key={s.id} className="ops-row">
              <div className="ops-row__main">
                <div className="ops-row__title" style={{ fontSize: 13.5 }}>
                  Session {s.id === currentSession ? '· this one' : ''}
                </div>
                <div className="ops-row__sub">
                  {s.ip ?? ''} · seen {ago(s.last_seen_at)} ago
                </div>
              </div>
              {s.id !== currentSession ? (
                <Btn kind="ghost" onClick={() => void api.post(`/api/auth/sessions/${s.id}/revoke`).then(load).catch(reporter('revoking the session'))}>
                  Revoke
                </Btn>
              ) : null}
            </div>
          ))}
        </Rows>
      </Section>

      {/* ── Box health ──────────────────────────────────────────────────── */}
      {health ? (
        <Section title="Box health" aside="dhs-ops-1">
          <Facts
            items={[
              { label: 'Disk', value: health.disk ? `${health.disk.freeGb} of ${health.disk.totalGb} GB free${health.disk.usedRatio != null ? ` · ${Math.round(health.disk.usedRatio * 100)}% used` : ''}` : '—' },
              { label: 'Memory free', value: `${health.memory.freeGb} / ${health.memory.totalGb} GB` },
              { label: 'Load', value: health.load.join(' · '), mono: true },
              { label: 'Running / queued', value: `${health.running} / ${health.queued}` },
              { label: 'Deployed', value: health.deployedRelease ?? 'unknown', mono: true },
              { label: 'Main is at', value: health.mainSha ? health.mainSha.slice(0, 8) : '—', mono: true },
            ]}
          />
          {health.attempts.length ? (
            <Rows>
              {health.attempts.map((a) => (
                <div key={a.jobId} className="ops-row" style={{ minHeight: 44 }} data-testid="health-attempt">
                  <div className="ops-row__main">
                    <div className="ops-row__title" style={{ fontSize: 13.5 }}>{a.title}</div>
                    <div className="ops-row__sub">
                      attempt {a.attempt} · {a.alive ? 'process alive' : 'process gone'}
                      {a.unitActive != null ? ` · unit ${a.unitActive ? 'active' : 'not active'}` : ''}
                      {a.since ? ` · since ${ago(a.since)} ago` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </Rows>
          ) : null}
          {history.length > 5 ? (
            <div className="ops-card" style={{ marginTop: 10 }}>
              <SparkRow label="CPU" unit="%" points={history.map((h) => h.cpu_pct ?? 0)} max={100} />
              <SparkRow label="Memory" unit="GB" points={history.map((h) => h.mem_used_gb ?? 0)} max={history[0]?.mem_total_gb ?? 16} />
              <SparkRow label="Disk" unit="GB" points={history.map((h) => h.disk_used_gb ?? 0)} max={history[0]?.disk_total_gb ?? 100} />
              <p className="ops-hint" style={{ margin: '6px 0 0' }}>Last 24 hours, one sample a minute.</p>
            </div>
          ) : null}
          {settings ? (
            <>
              <div style={{ height: 12 }} />
              <Field label="Retention on the box (days)">
                <div style={{ display: 'flex', gap: 8 }}>
                  {(['logsDays', 'shotsDays', 'artifactsDays'] as const).map((k) => (
                    <label key={k} style={{ flex: 1, fontSize: 11.5, color: 'var(--ops-ink-subtle)' }}>
                      {k === 'logsDays' ? 'logs' : k === 'shotsDays' ? 'screenshots' : 'artifacts'}
                      <input
                        className="ops-input"
                        style={{ marginTop: 4 }}
                        inputMode="numeric"
                        defaultValue={settings.retention[k]}
                        onBlur={(e) => put({ retention: { ...settings.retention, [k]: Math.max(1, Number(e.target.value) || settings.retention[k]) } })}
                      />
                    </label>
                  ))}
                </div>
              </Field>
              <p className="ops-hint">Screenshots and worktrees of closed jobs go after seven days; the disk says so on your phone at 80%.</p>
            </>
          ) : null}
          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn kind="quiet" onClick={() => void api.post('/api/system/cleanup').then(load).catch(reporter('the cleanup'))}>
              <Trash2 size={15} /> Clean up now
            </Btn>
            <Btn
              kind="quiet"
              onClick={() => {
                if (confirm('Restart the daemon? Attempts keep running; the app reconnects in a few seconds.')) {
                  void api.post('/api/system/restart').catch(reporter('the restart'));
                  setTimeout(load, 6000);
                }
              }}
            >
              <Power size={15} /> Restart daemon
            </Btn>
            <Btn kind="ghost" onClick={load}>
              <RefreshCw size={15} /> Refresh
            </Btn>
          </div>
        </Section>
      ) : null}
      {addOpen ? (
        <AddAccountSheet
          onClose={() => setAddOpen(false)}
          onAdded={() => {
            void api
              .get<{ accounts: typeof accounts; howToAdd: string }>('/api/accounts')
              .then((r) => setAccounts(r.accounts))
              .catch(() => undefined);
          }}
        />
      ) : null}
    </Screen>
  );
}

function SpendChart({ days }: { days: { day: string; usd: number }[] }) {
  if (!days.length) return <p className="ops-hint" style={{ margin: 0 }}>No spend recorded yet.</p>;
  const max = Math.max(...days.map((d) => d.usd), 0.01);
  const w = 100 / days.length;
  return (
    <div>
      <svg viewBox="0 0 100 30" preserveAspectRatio="none" style={{ width: '100%', height: 84, display: 'block' }} aria-label="Spend per day">
        {days.map((d, i) => {
          const h = Math.max(0.8, (d.usd / max) * 28);
          return (
            <rect key={d.day} x={i * w + w * 0.15} y={30 - h} width={w * 0.7} height={h} rx={0.6} fill="var(--ops-accent-bright)">
              <title>{`${d.day}: $${d.usd.toFixed(2)}`}</title>
            </rect>
          );
        })}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--ops-ink-subtle)', marginTop: 4 }}>
        <span>{days[0].day.slice(5)}</span>
        <span>
          {days[days.length - 1].day.slice(5)} · peak ${max.toFixed(2)}
        </span>
      </div>
    </div>
  );
}

function SparkRow(props: { label: string; unit: string; points: number[]; max: number }) {
  const pts = props.points;
  const max = Math.max(props.max, ...pts, 1);
  const step = pts.length > 1 ? 100 / (pts.length - 1) : 100;
  const line = pts.map((p, i) => `${(i * step).toFixed(2)},${(12 - (p / max) * 11).toFixed(2)}`).join(' ');
  const last = pts[pts.length - 1] ?? 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '72px 1fr auto', gap: 10, alignItems: 'center', minHeight: 30 }}>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ops-ink-muted)' }}>{props.label}</span>
      <svg viewBox="0 0 100 13" preserveAspectRatio="none" style={{ width: '100%', height: 26 }} aria-hidden>
        <polyline points={line} fill="none" stroke="var(--ops-run)" strokeWidth="0.9" vectorEffect="non-scaling-stroke" />
      </svg>
      <span style={{ fontFamily: 'var(--ops-mono)', fontSize: 11, color: 'var(--ops-ink-subtle)' }}>
        {Math.round(last * 10) / 10}
        {props.unit}
      </span>
    </div>
  );
}
